// ═══════════════════════════════════════════════════════════════════════════
// EMISSION SCHEDULE MANAGER - TASK 3
// Supply-based halving system (NOT time-based!)
// Rewards decrease as total supply emitted increases
// ═══════════════════════════════════════════════════════════════════════════

export const EMISSION_CONFIG = {
  TOTAL_ECOSYSTEM_REWARDS: 40_000_000, // 40M total to emit (out of 100M supply)
  TOTAL_SUPPLY: 100_000_000, // 100M total $DISTIL supply
  
  // Emission phases (supply-based, NOT time-based!)
  PHASES: [
    { supply: 0,          rate: 10,  phase: 1,  name: "Launch Phase" },
    { supply: 2_000_000,  rate: 9,   phase: 2,  name: "Growth Phase" },
    { supply: 4_000_000,  rate: 8,   phase: 3,  name: "Adoption Phase" },
    { supply: 6_000_000,  rate: 7,   phase: 4,  name: "Expansion Phase" },
    { supply: 8_000_000,  rate: 6,   phase: 5,  name: "Maturity Phase" },
    { supply: 10_000_000, rate: 5,   phase: 6,  name: "Stability Phase" },
    { supply: 15_000_000, rate: 4,   phase: 7,  name: "Consolidation Phase" },
    { supply: 20_000_000, rate: 3,   phase: 8,  name: "Late Stage Phase" },
    { supply: 25_000_000, rate: 2,   phase: 9,  name: "Sunset Phase" },
    { supply: 30_000_000, rate: 1,   phase: 10, name: "Final Phase" },
    { supply: 35_000_000, rate: 0.5, phase: 11, name: "Tail Emissions" },
  ]
};

/**
 * Get total amount of $DISTIL emitted so far
 * In demo mode, tracks in localStorage
 * In production, query from blockchain/indexer
 */
export function getTotalEmitted() {
  const emitted = parseFloat(localStorage.getItem('total_distil_emitted') || '0');
  return emitted;
}

/**
 * Track emission (called every time rewards are distributed)
 * @param {number} amount - Amount of $DISTIL emitted this purge
 */
export function trackEmission(amount) {
  const currentTotal = getTotalEmitted();
  const newTotal = currentTotal + amount;
  
  // Store new total
  localStorage.setItem('total_distil_emitted', newTotal.toString());
  
  // Track in history for velocity calculations
  const history = JSON.parse(localStorage.getItem('emission_history') || '[]');
  history.push({
    amount,
    total: newTotal,
    timestamp: Date.now()
  });
  
  // Keep last 100 emissions
  if (history.length > 100) history.shift();
  localStorage.setItem('emission_history', JSON.stringify(history));
  
  // Check if we just crossed a milestone
  checkMilestoneCrossed(currentTotal, newTotal);
  
  return newTotal;
}

/**
 * Get current emission phase based on total emitted
 */
export function getCurrentPhase() {
  const totalEmitted = getTotalEmitted();
  
  // Find the current phase (highest phase where emitted >= supply threshold)
  for (let i = EMISSION_CONFIG.PHASES.length - 1; i >= 0; i--) {
    if (totalEmitted >= EMISSION_CONFIG.PHASES[i].supply) {
      return EMISSION_CONFIG.PHASES[i];
    }
  }
  
  return EMISSION_CONFIG.PHASES[0]; // Default to Launch Phase
}

/**
 * Get current base reward rate (before bonuses)
 */
export function getBaseRewardRate() {
  const phase = getCurrentPhase();
  return phase.rate;
}

/**
 * Get next phase information
 */
export function getNextPhase() {
  const totalEmitted = getTotalEmitted();
  
  // Find next phase
  const nextPhase = EMISSION_CONFIG.PHASES.find(p => p.supply > totalEmitted);
  
  return nextPhase || null; // null if at final phase
}

/**
 * Get progress to next milestone
 */
export function getMilestoneProgress() {
  const totalEmitted = getTotalEmitted();
  const currentPhase = getCurrentPhase();
  const nextPhase = getNextPhase();
  
  if (!nextPhase) {
    return {
      isComplete: true,
      currentPhase,
      progress: 100,
      tokensToMilestone: 0,
      estimatedDays: null
    };
  }
  
  // Calculate progress
  const currentThreshold = currentPhase.supply;
  const nextThreshold = nextPhase.supply;
  const range = nextThreshold - currentThreshold;
  const progress = ((totalEmitted - currentThreshold) / range) * 100;
  const tokensToMilestone = nextThreshold - totalEmitted;
  
  // Estimate time based on recent emission velocity
  const velocity = calculateEmissionVelocity();
  const estimatedDays = velocity > 0 ? Math.ceil(tokensToMilestone / velocity) : null;
  
  return {
    isComplete: false,
    currentPhase,
    nextPhase,
    progress: Math.min(100, Math.max(0, progress)),
    tokensToMilestone,
    estimatedDays,
    totalEmitted
  };
}

/**
 * Calculate emission velocity (tokens per day)
 * @private
 */
function calculateEmissionVelocity() {
  const history = JSON.parse(localStorage.getItem('emission_history') || '[]');
  
  if (history.length < 2) return 0;
  
  // Use last 30 emissions to calculate velocity
  const recent = history.slice(-30);
  const firstEntry = recent[0];
  const lastEntry = recent[recent.length - 1];
  
  const tokensDiff = lastEntry.total - firstEntry.total;
  const timeDiff = lastEntry.timestamp - firstEntry.timestamp;
  const daysDiff = timeDiff / (1000 * 60 * 60 * 24);
  
  return daysDiff > 0 ? tokensDiff / daysDiff : 0;
}

/**
 * Check if milestone was just crossed
 * @private
 */
function checkMilestoneCrossed(oldTotal, newTotal) {
  // Find if we crossed a phase boundary
  const oldPhase = EMISSION_CONFIG.PHASES.find((p, i) => {
    const nextPhase = EMISSION_CONFIG.PHASES[i + 1];
    return oldTotal >= p.supply && (!nextPhase || oldTotal < nextPhase.supply);
  });
  
  const newPhase = EMISSION_CONFIG.PHASES.find((p, i) => {
    const nextPhase = EMISSION_CONFIG.PHASES[i + 1];
    return newTotal >= p.supply && (!nextPhase || newTotal < nextPhase.supply);
  });
  
  // If phase changed, we crossed a milestone!
  if (oldPhase && newPhase && oldPhase.phase !== newPhase.phase) {
    triggerMilestoneCelebration(newPhase);
  }
}

/**
 * Trigger milestone celebration
 * @private
 */
function triggerMilestoneCelebration(newPhase) {
  localStorage.setItem('milestone_just_reached', 'true');
  localStorage.setItem('milestone_phase', JSON.stringify(newPhase));
  localStorage.setItem('milestone_timestamp', Date.now().toString());
}

/**
 * Check if milestone was just reached (for MilestoneAlert component)
 */
export function checkForMilestone() {
  const justReached = localStorage.getItem('milestone_just_reached');
  
  if (justReached === 'true') {
    const phase = JSON.parse(localStorage.getItem('milestone_phase') || 'null');
    
    // Clear the flag
    localStorage.removeItem('milestone_just_reached');
    
    return phase;
  }
  
  return null;
}

/**
 * Get emission statistics
 */
export function getEmissionStats() {
  const totalEmitted = getTotalEmitted();
  const currentPhase = getCurrentPhase();
  const progress = getMilestoneProgress();
  const percentageEmitted = (totalEmitted / EMISSION_CONFIG.TOTAL_ECOSYSTEM_REWARDS) * 100;
  
  return {
    totalEmitted,
    totalToEmit: EMISSION_CONFIG.TOTAL_ECOSYSTEM_REWARDS,
    percentageEmitted,
    currentPhase,
    currentRate: currentPhase.rate,
    nextPhase: progress.nextPhase,
    progressToNext: progress.progress,
    tokensToNext: progress.tokensToMilestone,
    estimatedDays: progress.estimatedDays,
    isComplete: progress.isComplete
  };
}

/**
 * Get emission history for charts
 */
export function getEmissionHistory() {
  return JSON.parse(localStorage.getItem('emission_history') || '[]');
}

/**
 * Reset emissions (for testing/demo)
 */
export function resetEmissions() {
  localStorage.setItem('total_distil_emitted', '0');
  localStorage.setItem('emission_history', '[]');
  localStorage.removeItem('milestone_just_reached');
  localStorage.removeItem('milestone_phase');
}

// Export all functions
export default {
  EMISSION_CONFIG,
  getTotalEmitted,
  trackEmission,
  getCurrentPhase,
  getBaseRewardRate,
  getNextPhase,
  getMilestoneProgress,
  checkForMilestone,
  getEmissionStats,
  getEmissionHistory,
  resetEmissions
};
